import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ClipboardText, CircleNotch, Phone, WhatsappLogo, Check, Archive } from "@phosphor-icons/react";
import { toast } from "sonner";

const TABS = [["", "Todos"], ["novo", "Novos"], ["contactado", "Contactados"], ["arquivado", "Arquivados"]];
const ST = { novo: ["Novo", "border-primary text-primary"], contactado: ["Contactado", "border-emerald-600 text-emerald-700"], arquivado: ["Arquivado", "text-muted-foreground"] };

const wa = (phone, name) => {
  let d = (phone || "").replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  if (d.length === 7) d = "238" + d;
  return `https://wa.me/${d}?text=${encodeURIComponent(`Olá ${name}! Recebemos o seu pedido de orçamento pelo nosso site.`)}`;
};

function RequestCard({ r, isNew, onStatus, busy }) {
  const [label, cls] = ST[r.status] || ST.novo;
  return (
    <div className={`rounded-xl border bg-card p-4 ${isNew ? "border-primary ring-1 ring-primary/30" : "border-border"}`} data-testid={`quote-request-${r.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2"><span className="truncate font-semibold">{r.name}</span>{isNew && <Badge className="h-5 px-1.5 text-[10px]">Novo</Badge>}</div>
          <p className="mt-0.5 text-sm font-medium text-primary">{r.work_type}</p>
          <a href={`tel:${r.phone}`} className="mt-1 flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"><Phone size={14} />{r.phone}</a>
        </div>
        <Badge variant="outline" className={cls} data-testid={`quote-request-status-${r.id}`}>{label}</Badge>
      </div>
      {r.message && <p className="mt-2 whitespace-pre-line rounded-lg bg-muted px-3 py-2 text-sm">{r.message}</p>}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] text-muted-foreground">Recebido a {fmtDate(r.created_at)}</span>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="rounded-full" asChild data-testid={`quote-request-wa-${r.id}`}>
            <a href={wa(r.phone, r.name)} target="_blank" rel="noreferrer"><WhatsappLogo size={16} className="mr-1 text-emerald-600" /> WhatsApp</a>
          </Button>
          {r.status !== "contactado" && <Button size="sm" className="rounded-full" disabled={busy} onClick={() => onStatus(r, "contactado")} data-testid={`quote-request-contacted-${r.id}`}><Check size={14} className="mr-1" /> Contactado</Button>}
          {r.status !== "arquivado" && <Button size="sm" variant="ghost" className="rounded-full" disabled={busy} onClick={() => onStatus(r, "arquivado")} data-testid={`quote-request-archive-${r.id}`}><Archive size={14} className="mr-1" /> Arquivar</Button>}
        </div>
      </div>
    </div>
  );
}

export default function QuoteRequests() {
  const [items, setItems] = useState([]);
  const [newIds, setNewIds] = useState(new Set());
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/site/quote-requests", { params: { status: status || undefined } });
      setItems(data.items);
      const unseen = data.items.filter((r) => r.seen === false).map((r) => r.id);
      if (unseen.length) {
        setNewIds((prev) => new Set([...prev, ...unseen]));
        await api.post("/site/quote-requests/mark-seen");
        window.dispatchEvent(new Event("reservations-updated"));
      }
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const onStatus = async (r, st) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/site/quote-requests/${r.id}/status`, { status: st });
      setItems((list) => list.map((x) => (x.id === data.id ? data : x)));
      toast.success(st === "contactado" ? "Marcado como contactado" : "Pedido arquivado");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-5" data-testid="quote-requests-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Site público</p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Pedidos de orçamento</h1>
        <p className="mt-1 text-sm text-muted-foreground">Pedidos enviados pelos visitantes do seu site.</p>
      </div>
      <div className="flex gap-1 overflow-x-auto rounded-full bg-muted p-1 sm:w-fit">
        {TABS.map(([v, l]) => (
          <button key={v || "all"} onClick={() => setStatus(v)} data-testid={`quote-requests-tab-${v || "all"}`}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${status === v ? "bg-card shadow-sm" : "text-muted-foreground"}`}>{l}</button>
        ))}
      </div>
      {loading ? <div className="flex h-40 items-center justify-center"><CircleNotch size={24} className="animate-spin text-primary" /></div>
        : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card py-16 text-center" data-testid="quote-requests-empty">
            <ClipboardText size={40} className="text-muted-foreground" /><p className="text-muted-foreground">Sem pedidos de orçamento por agora.</p>
          </div>
        ) : <div className="grid gap-3 lg:grid-cols-2">{items.map((r) => <RequestCard key={r.id} r={r} isNew={newIds.has(r.id)} onStatus={onStatus} busy={busy} />)}</div>}
    </div>
  );
}
